import type { TaskNode } from "./types";
import { currentPath, rootIsSolo, trimPath } from "./paths";
import { newTask } from "./model";
import { locateOrNull, findParent, isDescendant } from "./tree";
import { fmt } from "./format";

// Minimal Jira-importable CSV. Only the columns the round-trip needs:
//   Summary · Issue key · Issue Type · Status · Sprint · Components · Original estimate
// (Sprint carries the task version; Components carries the component; Original estimate is
// the leaf estimate in seconds — Jira's format, 1 day = 8h; Status is "Done" for completed
// leaves). We export leaf tasks (non-disabled) as Type=Task; Issue key is blank until Jira
// assigns one; re-importing a Jira export attaches keys (and syncs Done/Closed → completion).
const COLUMNS = ["Summary", "Issue key", "Issue Type", "Status", "Sprint", "Components", "Original estimate"];
const SECONDS_PER_DAY = 8 * 3600; // Jira default working day = 8h

// Jira statuses we treat as "completed" on import/compare (case-insensitive).
// "Testing" and "In Review" count as done: the dev work is finished and only verification is
// outstanding, which is what a completed task means in a plan.
function isDoneStatus(s: string): boolean { return /^(done|closed|testing|in review)$/i.test(s.trim()); }

export const DEFAULT_DELIM = ">"; // level delimiter for the Summary breadcrumb ("A > B > C")

// Issue types that are containers rather than work, and so can never be a task in the plan.
// Everything else — Task, Sub-task, Bug, Story, Incident, Spike, Improvement, … — is real work
// and is matched. This used to be an allow-list of exactly "Task", which silently dropped every
// bug on the floor: not matched, not created, and not even reported as unmatched.
const SKIP_TYPES = new Set(["epic"]);
function skippableType(type: string): boolean { return !!type && SKIP_TYPES.has(type.trim().toLowerCase()); }

// Build a ticket URL from a configured template: "{key}" is substituted, or the key is appended
// when the template has no placeholder. Empty template (or key) → "" = no link. Shared so the
// grid's key badge and the HTML export produce the exact same href.
export function ticketUrl(template: string, key: string): string {
  const t = (template || "").trim();
  if (!t || !key) return "";
  return t.includes("{key}") ? t.replace(/\{key\}/g, encodeURIComponent(key)) : t + encodeURIComponent(key);
}

// the full root→node name chain (untrimmed), e.g. ["Root","A","Leaf"]
function nameChain(tree: TaskNode[], id: string): string[] {
  const names: string[] = [];
  let cur: string | null = id;
  while (cur) {
    const loc = locateOrNull(tree, cur);
    if (!loc) break;
    names.unshift(loc.node.name);
    const p = findParent(tree, cur);
    cur = p ? p.parentNode.id : null;
  }
  return names;
}
// the Summary path for a task: lone-root-trimmed name chain joined by the delimiter
function jiraPath(tree: TaskNode[], id: string, delim: string): string {
  return trimPath(tree, nameChain(tree, id)).join(` ${delim} `);
}
// Summaries are hand-typed breadcrumbs and people use whichever arrow is to hand — › » → >> all
// mean "the delimiter". Fold them to the configured one before splitting or comparing, or a
// "A › B › C" summary stays one segment and its whole breadcrumb becomes the task's name.
function canonDelims(summary: string, delim: string): string {
  return summary.replace(/\s*(?:›|»|→|>>)\s*/g, ` ${delim} `);
}
// split a Summary ("A > B > C") into trimmed, non-empty segments on the delimiter
function splitSummary(summary: string, delim: string): string[] {
  return canonDelims(summary, delim).split(delim).map((s) => s.trim()).filter(Boolean);
}
// A forgiving key for comparing a Summary to a task path: canonical delimiters, each segment
// trimmed, runs of whitespace collapsed, case-folded. Only used as a *fallback* — an exact
// match is still preferred, so paths that already agree byte-for-byte behave exactly as before.
function pathKey(s: string, delim: string): string {
  return canonDelims(s, delim).split(delim).map((seg) => seg.trim().replace(/\s+/g, " ")).join(` ${delim} `).toLowerCase();
}
// re-add a trimmed lone root so segments resolve/create against the real tree root
function fullSegments(tree: TaskNode[], segments: string[]): string[] {
  if (rootIsSolo(tree) && tree.length && segments[0] !== tree[0].name) return [tree[0].name, ...segments];
  return segments;
}

function csvCell(s: string): string {
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

// `newOnly` → export only tasks not yet in Jira (no issue key), for a first-time import of new work.
// `versionedOnly` holds back work that hasn't been scheduled into a release yet: a task you've
// just jotted down has no version, and filing it in Jira before you've decided when it ships only
// creates a ticket you have to remember to close.
export function toJiraCsv(
  tree: TaskNode[], delim: string = DEFAULT_DELIM, newOnly = false, versionedOnly = false,
): string {
  const rows: string[][] = [COLUMNS];
  (function walk(list: TaskNode[]) {
    for (const n of list) {
      if (n.disabled) continue; // disabled work isn't a Jira issue
      if (n.children.length === 0) {
        if (newOnly && n.jiraKey) continue; // skip tasks already in Jira
        if (versionedOnly && !(n.version || "").trim()) continue; // not scheduled yet → paused
        const est = n.estimate > 0 ? String(Math.round(n.estimate * SECONDS_PER_DAY)) : "";
        // Summary = full breadcrumb path (lone root trimmed), same rule as the views
        rows.push([jiraPath(tree, n.id, delim), n.jiraKey || "", "Task", n.done ? "Done" : "", n.version || "", n.component || "", est]);
      } else walk(n.children);
    }
  })(tree);
  return rows.map((r) => r.map(csvCell).join(",")).join("\n");
}

// RFC-4180-ish parser: handles quoted fields with commas, quotes and newlines.
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], cell = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else inQ = false; }
      else cell += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ",") { row.push(cell); cell = ""; }
    else if (ch === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; }
    else if (ch === "\r") { /* skip CR */ }
    else cell += ch;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

export interface JiraRow {
  summary: string;
  key: string;
  type: string;
  status: string | null;    // Jira "Status" column, if present (Done/Closed → completed)
  sprint: string | null;    // null when the CSV has no Sprint column
  component: string | null; // Jira "Components" column, if present
  estimate: number | null;  // "Original estimate" seconds → days, if present
  fixVersions: string[] | null; // Jira "Fix Version/s" values (multi-valued) — null when absent
  // Who raised the ticket. Shown in the import review so you can judge what to bring in; it is
  // deliberately NOT stored on a task — that would put colleagues' names into the plan file and
  // from there into the JSON and the read-only HTML export, which is meant for outside readers.
  reporter: string | null;
  // Whether the CSV had an estimate column at all. `estimate` is null both when the column is
  // missing and when the cell is blank, and the two mean different things: a blank cell is Jira
  // saying "no estimate" (it displays 0m), while a missing column means we simply don't know.
  // Compare treats blank as 0 and tells you to go set it; the import must NOT, or it would offer
  // to zero out every estimate Jira never received.
  hasEstimateColumn: boolean;
}

// Jira's "Fix Version/s" field is multi-valued; in a CSV export each value gets its OWN
// column, all sharing the same header. We collect every such column into a list.
const FIX_VERSION_HEADERS = new Set(["fix version/s", "fix versions", "fix version"]);
// Jira names this column several ways and prefixes aggregate columns with "Σ" — a plain
// indexOf("Original estimate") missed "Σ Original Estimate" entirely, which silently made every
// row estimate-less and skipped the estimate comparison for a whole file.
const ESTIMATE_HEADERS = new Set(["original estimate", "original estimate (seconds)", "time estimate"]);
function isEstimateHeader(h: string): boolean {
  return ESTIMATE_HEADERS.has(h.replace(/^[^A-Za-z]+/, "").trim().toLowerCase());
}

// parse a Jira CSV (full 268-column export or our minimal one) by column header
export function parseJiraCsv(text: string): JiraRow[] {
  const rows = parseCsv(text).filter((r) => r.some((c) => c.trim() !== ""));
  if (!rows.length) return [];
  const header = rows[0].map((h) => h.trim());
  const at = (name: string) => header.indexOf(name);
  const iS = at("Summary"), iK = at("Issue key"), iT = at("Issue Type");
  const iSt = at("Status"), iSp = at("Sprint"), iC = at("Components");
  const iE = header.findIndex(isEstimateHeader);
  const iR = at("Reporter");
  const fixIdx: number[] = []; // all Fix Version/s columns (repeated header)
  header.forEach((h, i) => { if (FIX_VERSION_HEADERS.has(h.toLowerCase())) fixIdx.push(i); });
  return rows.slice(1).map((r) => {
    const secs = iE >= 0 ? parseFloat(r[iE] ?? "") : NaN;
    return {
      summary: iS >= 0 ? (r[iS] ?? "").trim() : "",
      key: iK >= 0 ? (r[iK] ?? "").trim() : "",
      type: iT >= 0 ? (r[iT] ?? "").trim() : "",
      status: iSt >= 0 ? (r[iSt] ?? "").trim() : null,
      sprint: iSp >= 0 ? (r[iSp] ?? "").trim() : null,
      component: iC >= 0 ? (r[iC] ?? "").trim() : null,
      estimate: Number.isFinite(secs) ? secs / SECONDS_PER_DAY : null,
      fixVersions: fixIdx.length ? fixIdx.map((i) => (r[i] ?? "").trim()).filter(Boolean) : null,
      reporter: iR >= 0 ? (r[iR] ?? "").trim() || null : null,
      hasEstimateColumn: iE >= 0,
    };
  });
}

// find-or-create a task chain for a breadcrumb path ("A > B > C"), returning the leaf
function upsertPath(tree: TaskNode[], segments: string[]): TaskNode | null {
  if (!segments.length) return null;
  let list = tree;
  let node: TaskNode | null = null;
  segments.forEach((seg) => {
    let found = list.find((n) => n.name === seg);
    if (!found) { found = newTask(seg); found.estimate = 0; list.push(found); }
    node = found;
    list = found.children;
  });
  return node;
}

// Link Jira keys onto tasks, matched by Issue key first then by Summary (full path).
//   mode "map"    — set the Issue key on matches (and mark completed when Status is
//                   Done/Closed); nothing else, no creation.
//   mode "attach" — same, plus CREATE unmatched rows (rebuilding the A > B > C
//                   hierarchy) with their key, status, version (Sprint), component and estimate.
export interface JiraLink {
  id: string;        // the task id (so the UI can apply an estimate update)
  key: string;
  path: string;
  done: boolean;
  jiraEstimate: number | null; // "Original estimate" from the CSV in days (null if absent/blank)
  planEstimate: number;        // the task's current estimate in days
}
export interface JiraApplyResult {
  matched: number;
  created: number;
  unmatched: string[];
  linked: JiraLink[];  // keys mapped onto existing tasks
  createdLinks: JiraLink[]; // keys on tasks created (attach mode)
}

export function applyJiraCsv(
  tree: TaskNode[], rows: JiraRow[], mode: "map" | "attach", delim: string = DEFAULT_DELIM,
): JiraApplyResult {
  const all: TaskNode[] = [];
  (function walk(list: TaskNode[]) { for (const n of list) { all.push(n); walk(n.children); } })(tree);
  const pathById = new Map<string, string>();
  for (const n of all) pathById.set(n.id, jiraPath(tree, n.id, delim));

  const used = new Set<string>();
  let matched = 0, created = 0;
  const unmatched: string[] = [];
  const linked: JiraLink[] = [], createdLinks: JiraLink[] = [];
  for (const row of rows) {
    if (skippableType(row.type)) continue; // containers (Epics) aren't plan tasks
    let target: TaskNode | undefined;
    if (row.key) target = all.find((n) => !used.has(n.id) && n.jiraKey === row.key);
    if (!target && row.summary) target = all.find((n) => !used.has(n.id) && pathById.get(n.id) === row.summary);
    if (target) {
      used.add(target.id);
      if (row.key) target.jiraKey = row.key; // link the key…
      const done = !!row.status && isDoneStatus(row.status);
      if (done) target.done = true; // …and sync Done/Closed → completed
      matched++;
      if (row.key) linked.push({
        id: target.id, key: row.key, path: pathById.get(target.id) || target.name, done,
        jiraEstimate: row.estimate, planEstimate: target.estimate,
      });
      continue;
    }
    // map mode never creates — only attach rebuilds the A > B > C hierarchy for unmatched rows
    if (mode === "attach" && row.summary) {
      const leaf = upsertPath(tree, fullSegments(tree, splitSummary(row.summary, delim)));
      if (leaf) {
        if (row.key) leaf.jiraKey = row.key;
        const done = !!row.status && isDoneStatus(row.status);
        if (done) leaf.done = true;
        if (row.sprint) leaf.version = row.sprint;
        if (row.component) leaf.component = row.component;
        if (row.estimate != null) leaf.estimate = row.estimate;
        all.push(leaf); used.add(leaf.id); pathById.set(leaf.id, jiraPath(tree, leaf.id, delim));
        created++;
        createdLinks.push({
          id: leaf.id, key: row.key || "", path: pathById.get(leaf.id)!, done,
          jiraEstimate: row.estimate, planEstimate: leaf.estimate,
        });
      }
    } else if (row.summary || row.key) {
      unmatched.push(row.summary || row.key);
    }
  }
  return { matched, created, unmatched, linked, createdLinks };
}

/* ---------- two-phase import: plan (pure diff) → apply (only selected) ---------- */
// Fields a Jira row can change on a matched task. Values are raw (the UI formats them).
// "name" renames the task to the Summary's leaf; a structural move is tracked separately.
export type JiraField = "jiraKey" | "name" | "done" | "version" | "component" | "estimate";
export interface JiraChange {
  field: JiraField;
  from: string | number | boolean;
  to: string | number | boolean;
  selected: boolean; // toggled by the review UI; apply honours it
}
// a reparent: the task should sit under `parentSegments` (full, root-included) to match the
// Summary hierarchy. Intermediate parents are created if missing when applied.
export interface JiraMove {
  fromPath: string;      // current breadcrumb (display)
  toPath: string;        // desired breadcrumb (display)
  parentSegments: string[]; // full name chain of the desired parent (for find-or-create)
  selected: boolean;
}
export interface JiraMatchPlan {
  id: string;          // task id
  key: string;         // the row's key (or the task's existing one)
  path: string;        // task breadcrumb
  changes: JiraChange[]; // fields that differ (empty matches are omitted)
  move: JiraMove | null; // set when the task must be relocated to match the Summary path
}
// A match the import will not act on: either everything already agrees, or (keys mode) the task
// already carries a key. `note` is set only when that existing key differs from the CSV's, which
// is a real conflict worth seeing rather than a silent skip.
export interface JiraInSync {
  id: string;
  key: string;
  path: string;
  note?: string;
}
export interface JiraCreatePlan {
  // Who raised the ticket, shown only here: for a task that already exists in the plan you know
  // who owns it, but for one you're about to create from a ticket it's the useful context for
  // deciding whether to take it. Never written onto the task — that would carry colleagues' names
  // into the plan file and from there into the JSON and the shared HTML export.
  reporter?: string;
  path: string;
  segments: string[];  // full (root-included) chain to find-or-create
  key: string;
  done: boolean;
  version: string;
  component: string;
  estimate: number | null;
  selected: boolean;
}
// map   — update matched tasks only
// attach— also create the unmatched issues, rebuilding the hierarchy
// keys  — link only: write jiraKey onto matched tasks that haven't got one, nothing else
export type JiraMode = "map" | "attach" | "keys";

export interface JiraPlan {
  mode: JiraMode;
  matchedCount: number;       // total tasks matched (incl. ones with no changes)
  matches: JiraMatchPlan[];   // matched tasks that have at least one change/move
  creates: JiraCreatePlan[];  // new tasks (attach mode only)
  unmatched: string[];
  inSync: JiraInSync[];       // matched, but nothing to change — the "already linked" bucket
  ignoredType: string[];      // rows ignored for their issue type (Epics); labels, so we can list them
}

function matchTarget(all: TaskNode[], used: Set<string>, pathById: Map<string, string>, row: JiraRow, delim: string): TaskNode | undefined {
  let t: TaskNode | undefined;
  if (row.key) t = all.find((n) => !used.has(n.id) && n.jiraKey === row.key); // 1) by Issue key
  if (!t && row.summary) t = all.find((n) => !used.has(n.id) && pathById.get(n.id) === row.summary); // 2) by path/name
  // 3) by normalised path — a different arrow character, stray spacing or casing shouldn't cost
  // you the match. Deliberately last, so an exact hit always wins and today's behaviour stands.
  if (!t && row.summary) {
    const want = pathKey(row.summary, delim);
    t = all.find((n) => !used.has(n.id) && pathKey(pathById.get(n.id) || "", delim) === want);
  }
  return t;
}

// diff a matched task against a row — one JiraChange per scalar field that would change
function diffRow(node: TaskNode, row: JiraRow, leafName: string): JiraChange[] {
  const ch: JiraChange[] = [];
  if (row.key && row.key !== node.jiraKey)
    ch.push({ field: "jiraKey", from: node.jiraKey, to: row.key, selected: true });
  if (leafName && leafName !== node.name)
    ch.push({ field: "name", from: node.name, to: leafName, selected: true });
  if (row.status != null && row.status !== "") {
    const to = isDoneStatus(row.status);
    if (to !== node.done) ch.push({ field: "done", from: node.done, to, selected: true });
  }
  if (row.sprint && row.sprint !== node.version)
    ch.push({ field: "version", from: node.version, to: row.sprint, selected: true });
  if (row.component && row.component !== node.component)
    ch.push({ field: "component", from: node.component, to: row.component, selected: true });
  if (row.estimate != null && row.estimate !== node.estimate)
    ch.push({ field: "estimate", from: node.estimate, to: row.estimate, selected: true });
  return ch;
}

// Compute what an import WOULD do, without mutating the tree. The UI reviews/toggles the
// returned plan, then calls applyJiraPlan to apply only the selected changes.
export function planJiraImport(tree: TaskNode[], rows: JiraRow[], mode: JiraMode, delim: string = DEFAULT_DELIM): JiraPlan {
  const all: TaskNode[] = [];
  (function walk(list: TaskNode[]) { for (const n of list) { all.push(n); walk(n.children); } })(tree);
  const pathById = new Map<string, string>();
  for (const n of all) pathById.set(n.id, jiraPath(tree, n.id, delim));

  const used = new Set<string>();
  const matches: JiraMatchPlan[] = [];
  const creates: JiraCreatePlan[] = [];
  const unmatched: string[] = [];
  const inSync: JiraInSync[] = [];
  const ignoredType: string[] = [];
  let matchedCount = 0;
  const keysOnly = mode === "keys"; // link tickets to tasks and nothing else
  for (const row of rows) {
    if (skippableType(row.type)) { // containers only — see SKIP_TYPES
      ignoredType.push(row.summary || row.key);
      continue;
    }
    const target = matchTarget(all, used, pathById, row, delim);
    if (target) {
      used.add(target.id);
      matchedCount++;
      const segs = row.summary ? splitSummary(row.summary, delim) : [];
      const leafName = segs.length ? segs[segs.length - 1] : "";
      // keys mode is a pure backfill: the key is the only field it may write, and only onto a
      // task that hasn't got one. Fixing a *wrong* key stays a manual job (⌥-click the badge).
      const changes = keysOnly
        ? (target.jiraKey ? [] : diffRow(target, row, "").filter((c) => c.field === "jiraKey"))
        : diffRow(target, row, leafName);
      // reparent if the desired ancestor chain differs from the current one (matched by key
      // but moved in the project, e.g. Jira "A > B > C" while the task sits at "A > C")
      let move: JiraMove | null = null;
      if (segs.length > 1 && !keysOnly) {
        const desiredParentFull = fullSegments(tree, segs).slice(0, -1);
        const currentParentFull = nameChain(tree, target.id).slice(0, -1);
        if (desiredParentFull.join(" ") !== currentParentFull.join(" ")) {
          move = {
            fromPath: pathById.get(target.id) || target.name,
            toPath: row.summary,
            parentSegments: desiredParentFull,
            selected: true,
          };
        }
      }
      if (changes.length || move) {
        matches.push({ id: target.id, key: row.key || target.jiraKey, path: pathById.get(target.id) || target.name, changes, move });
      } else {
        // matched, but there is nothing to do — surfaced so the dialog can account for every row
        // instead of leaving the difference between "matched" and "changed" unexplained.
        inSync.push({
          id: target.id, key: row.key || target.jiraKey, path: pathById.get(target.id) || target.name,
          note: keysOnly && target.jiraKey && row.key && target.jiraKey !== row.key
            ? `already ${target.jiraKey} — left as is` : undefined,
        });
      }
      continue;
    }
    if (mode === "attach" && row.summary) {
      const segments = fullSegments(tree, splitSummary(row.summary, delim));
      if (segments.length) creates.push({
        reporter: row.reporter || undefined,
        path: row.summary, segments, key: row.key || "",
        done: !!row.status && isDoneStatus(row.status),
        version: row.sprint || "", component: row.component || "", estimate: row.estimate, selected: true,
      });
    } else if (row.summary || row.key) {
      unmatched.push(row.summary || row.key);
    }
  }
  return { mode, matchedCount, matches, creates, unmatched, inSync, ignoredType };
}

/* ---------- compare: MPP-as-truth vs a Jira CSV export (read-only) ---------- */
// Phrased as "what to change in Jira to match the plan": for each ticket matched to a
// task, the fields whose Jira value drifted from MPP. Direction is jira → plan (the
// inverse of diffRow); jiraKey is excluded (tickets are matched by it).
export type CompareField = "name" | "status" | "version" | "component" | "estimate";
export interface CompareChange {
  field: CompareField;
  jira: string | number | boolean; // current Jira value (raw; the UI formats it)
  plan: string | number | boolean; // desired MPP value — what Jira should become
}
export interface CompareTicket {
  id: string;   // matched task id
  key: string;  // Jira key (row's, or the task's existing one)
  path: string; // task breadcrumb (MPP)
  changes: CompareChange[];
}
export interface JiraCompare {
  update: CompareTicket[]; // matched tickets whose Jira values differ from MPP
  matchedCount: number;    // total tickets matched (incl. ones already in sync)
  unmatched: string[];     // tickets with no task in the plan — "what haven't I planned?"
  ignoredType: string[];   // rows ignored for their issue type (Epics)
}
// which plan tasks to compare: everything, only completed, or only still-planned (not done)
export type CompareScope = "all" | "done" | "planned";

// diff a matched task against a row, jira → plan — one CompareChange per drifted field
function compareRow(node: TaskNode, row: JiraRow, planPath: string, delim: string): CompareChange[] {
  const ch: CompareChange[] = [];
  // Compare the WHOLE breadcrumb, not just the leaf. `toJiraCsv` emits a task's full path as the
  // Summary, so the full path *is* the correct summary for that ticket — and comparing only the
  // leaf meant a ticket filed under the wrong parent ("… > Video Edit > Preview" against a plan
  // path of "… > Videos Management > Preview") reported nothing at all. Compared through pathKey
  // so a different arrow, stray spacing or casing isn't announced as a rename.
  if (row.summary && pathKey(row.summary, delim) !== pathKey(planPath, delim)) {
    ch.push({ field: "name", jira: row.summary, plan: planPath });
  }
  if (row.status != null && row.status !== "") {
    if (isDoneStatus(row.status) !== node.done)
      ch.push({ field: "status", jira: row.status, plan: node.done });
  }
  // version: our single version must appear among the ticket's (possibly many) Jira Fix
  // versions. Only checked when the CSV has a Fix Version/s column and the plan sets a version.
  if (row.fixVersions != null && node.version && !row.fixVersions.includes(node.version))
    ch.push({ field: "version", jira: row.fixVersions.join(", "), plan: node.version });
  if (row.component != null && (row.component || "") !== (node.component || ""))
    ch.push({ field: "component", jira: row.component, plan: node.component });
  // Blank counts as 0 here — that is literally what Jira shows for an unset original estimate
  // ("0m"), and "the plan says 1d, Jira says 0m" is exactly the kind of thing this tab exists to
  // tell you. Gated on the column existing so a CSV without one reports nothing rather than
  // claiming every estimate is 0.
  if (row.hasEstimateColumn && (row.estimate ?? 0) !== node.estimate)
    ch.push({ field: "estimate", jira: row.estimate ?? 0, plan: node.estimate });
  return ch;
}

// Compare a Jira CSV export against the plan (never mutates). Disabled tasks are
// excluded from matching (they aren't Jira issues — same rule as toJiraCsv).
export function compareJira(tree: TaskNode[], rows: JiraRow[], delim: string = DEFAULT_DELIM, scope: CompareScope = "all"): JiraCompare {
  const all: TaskNode[] = [];
  (function walk(list: TaskNode[]) {
    for (const n of list) { if (n.disabled) continue; all.push(n); walk(n.children); }
  })(tree);
  const pathById = new Map<string, string>();
  for (const n of all) pathById.set(n.id, jiraPath(tree, n.id, delim));

  const used = new Set<string>();
  const update: CompareTicket[] = [];
  const unmatched: string[] = [];
  const ignoredType: string[] = [];
  let matchedCount = 0;
  for (const row of rows) {
    if (skippableType(row.type)) { ignoredType.push(row.summary || row.key); continue; } // see SKIP_TYPES
    const target = matchTarget(all, used, pathById, row, delim);
    // a ticket with no task behind it answers "what's in Jira that I haven't planned?" — it used
    // to be dropped here, so Compare could only ever tell you half the story
    if (!target) { if (row.summary || row.key) unmatched.push(row.summary || row.key); continue; }
    // view filter: keep only tasks matching the chosen completion scope (by the PLAN's done state)
    if (scope !== "all" && (scope === "done") !== !!target.done) continue;
    used.add(target.id);
    matchedCount++;
    const segs = row.summary ? splitSummary(row.summary, delim) : [];
    const leafName = segs.length ? segs[segs.length - 1] : "";
    const changes = compareRow(target, row, pathById.get(target.id) || target.name, delim);
    if (changes.length)
      update.push({ id: target.id, key: row.key || target.jiraKey, path: pathById.get(target.id) || target.name, changes });
  }
  return { update, matchedCount, unmatched, ignoredType };
}

// One shareable plain-text instruction per drifted field (parallels the client's
// compareActionHtml — same wording, no markup). The plan value is what Jira should become.
export function compareChangeText(c: CompareChange): string {
  switch (c.field) {
    case "name":
      return `Rename to "${c.plan}" (Jira: ${c.jira})`;
    case "status":
      return c.plan
        ? `Set status to Done (currently ${c.jira})`
        : `Reopen — set a non-done status (currently ${c.jira})`;
    case "version":
      return `Add Fix version ${c.plan} (Jira: ${c.jira || "none"})`;
    case "component":
      return c.plan
        ? `Assign component ${c.plan} (Jira: ${c.jira || "—"})`
        : `Clear component (Jira: ${c.jira})`;
    case "estimate":
      return `Adjust estimate to ${fmt(Number(c.plan))}d (Jira: ${fmt(Number(c.jira))}d)`;
  }
}

// A shareable plain-text summary of a compare result, for pasting to the team.
// Mirrors what the Compare tab shows on screen (plan = source of truth).
export function compareReport(cmp: JiraCompare, opts: { projectName?: string; scope?: CompareScope } = {}): string {
  const name = (opts.projectName || "").trim();
  const scopeNote = opts.scope === "done" ? " · done tasks only" : opts.scope === "planned" ? " · planned tasks only" : "";
  const lines: string[] = [(name ? `Jira compare — ${name}` : "Jira compare") + scopeNote];
  const n = cmp.update.length;
  if (!n) {
    lines.push(cmp.matchedCount
      ? `All ${cmp.matchedCount} matched ticket${cmp.matchedCount === 1 ? "" : "s"} already match the plan.`
      : "No tickets matched — check the CSV has Issue key / Summary columns.");
    return lines.join("\n");
  }
  lines.push(`Plan is the source of truth. ${n} ticket${n === 1 ? "" : "s"} to update in Jira (${cmp.matchedCount} matched).`);
  for (const t of cmp.update) {
    lines.push("");
    lines.push(`${t.key ? t.key + "  " : ""}${t.path}`);
    for (const c of t.changes) lines.push(`  - ${compareChangeText(c)}`);
  }
  return lines.join("\n");
}

export interface JiraApplyReport {
  changed: { key: string; path: string; fields: string[] }[]; // tasks actually updated (fields incl. "move")
  created: { key: string; path: string }[];
  unmatched: string[];
}

// move a task under `parent` (or to root when null); creates no cycles, no-ops if already there
function reparentNode(tree: TaskNode[], id: string, parent: TaskNode | null): boolean {
  const loc = locateOrNull(tree, id);
  if (!loc) return false;
  if (parent && (parent.id === id || isDescendant(tree, id, parent.id))) return false; // guard cycles
  const dest = parent ? parent.children : tree;
  if (loc.parentList === dest) return false; // already the right parent
  loc.parentList.splice(loc.index, 1);
  dest.push(loc.node);
  return true;
}

// Apply a reviewed plan: only changes/creates with selected=true are written.
export function applyJiraPlan(tree: TaskNode[], plan: JiraPlan): JiraApplyReport {
  const changed: JiraApplyReport["changed"] = [];
  for (const m of plan.matches) {
    const loc = locateOrNull(tree, m.id);
    if (!loc) continue;
    const node = loc.node;
    const fields: string[] = [];
    for (const c of m.changes) {
      if (!c.selected) continue;
      if (c.field === "jiraKey") node.jiraKey = String(c.to);
      else if (c.field === "name") node.name = String(c.to);
      else if (c.field === "done") node.done = !!c.to;
      else if (c.field === "version") node.version = String(c.to);
      else if (c.field === "component") node.component = String(c.to);
      else if (c.field === "estimate") node.estimate = Number(c.to);
      fields.push(c.field);
    }
    if (m.move && m.move.selected) {
      const parent = m.move.parentSegments.length ? upsertPath(tree, m.move.parentSegments) : null;
      if (reparentNode(tree, m.id, parent)) fields.push("move");
    }
    if (fields.length) changed.push({ key: m.key, path: currentPath(tree, m.id), fields });
  }
  const created: JiraApplyReport["created"] = [];
  for (const cr of plan.creates) {
    if (!cr.selected) continue;
    const leaf = upsertPath(tree, cr.segments);
    if (!leaf) continue;
    if (cr.key) leaf.jiraKey = cr.key;
    if (cr.done) leaf.done = true;
    if (cr.version) leaf.version = cr.version;
    if (cr.component) leaf.component = cr.component;
    if (cr.estimate != null) leaf.estimate = cr.estimate;
    created.push({ key: cr.key, path: currentPath(tree, leaf.id) });
  }
  return { changed, created, unmatched: plan.unmatched };
}
